import { Burger, Container, Drawer, Group, Image, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { Link as RemixLink, useLocation } from 'react-router';
import { MarketingLanguageSwitcher } from '#marketing/components/MarketingLanguageSwitcher.jsx';
import { localePath, parseLocaleFromPath } from '#marketing/lib/marketing-locale.js';
import { useMarketingLocale } from '#marketing/lib/use-marketing-locale.js';
import classes from './Header.module.css';

const navItems = [
  { path: '/', labelKey: 'marketing.nav.home' },
  { path: '/features', labelKey: 'marketing.nav.features' },
  { path: '/how-it-works', labelKey: 'marketing.nav.howItWorks' },
  { path: '/guides', labelKey: 'marketing.nav.guides' },
  { path: '/about', labelKey: 'marketing.nav.about' },
  { path: '/faq', labelKey: 'marketing.nav.faq' },
  { path: '/contact', labelKey: 'marketing.nav.contact', cta: true },
];

/**
 * Apex marketing site header with responsive navigation and language switcher.
 */
export function Header() {
  const { t } = useTranslation('common');
  const location = useLocation();
  const locale = useMarketingLocale();
  const { pathWithoutLocale } = parseLocaleFromPath(location.pathname);
  const [opened, { toggle, close }] = useDisclosure(false);

  const isActive = (path) =>
    pathWithoutLocale === path ||
    (path !== '/' && pathWithoutLocale.startsWith(`${path}/`));

  const navLinks = navItems.map((item) => {
    const href = localePath(locale, item.path);
    const active = isActive(item.path);
    const className = [
      classes.link,
      item.cta && classes.linkCta,
      active && classes.linkActive,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <RemixLink key={item.path} to={href} className={className} onClick={close}>
        {t(item.labelKey)}
      </RemixLink>
    );
  });

  return (
    <header className={classes.header}>
      <Container size="lg">
        <Group justify="space-between" className={classes.inner} wrap="nowrap">
          <Group gap="xs">
            <Image
              src="/logo-transparent-header.png"
              alt="PAC Hub"
              radius="md"
              h={32}
              w="auto"
              fit="contain"
            />
            <Text
              component={RemixLink}
              to={localePath(locale, '/')}
              fw={700}
              size="lg"
              c="pachubColourScheme.7"
              className={classes.logo}
            >
              PAC Hub
            </Text>
          </Group>
          <Group gap="md" wrap="nowrap" visibleFrom="sm" className={classes.desktopNav}>
            {navLinks}
            <MarketingLanguageSwitcher />
          </Group>

          <Burger
            opened={opened}
            onClick={toggle}
            hiddenFrom="sm"
            size="sm"
            aria-label={t('marketing.nav.menuToggle')}
          />
        </Group>
      </Container>

      <Drawer
        opened={opened}
        onClose={close}
        hiddenFrom="sm"
        title="PAC Hub"
        padding="md"
      >
        <Stack gap="sm">
          {navLinks}
          <MarketingLanguageSwitcher onNavigate={close} />
        </Stack>
      </Drawer>
    </header>
  );
}
